import { EffectComposer, Bloom, Vignette, LUT, Outline, SMAA, N8AO } from '@react-three/postprocessing'
import { BlendFunction, KernelSize, SMAAPreset, OutlineEffect } from 'postprocessing'
import { Mesh, Object3D } from 'three'
import { useThree } from '@react-three/fiber'
import { useEffect, useMemo, useRef } from 'react'
import { defaultLutTexture, isMarkerVisibleOnCam } from '../utils'

const isMobile = navigator.maxTouchPoints > 0

const OUTLINE_EDGE_STRENGTH = 2
const OUTLINE_PULSE_SPEED = 0.2
const OUTLINE_VISIBLE_COLOR = 0xffffff
const OUTLINE_HOVER_COLOR = 0xfcec42

interface EffectsProps {
  interactables: Object3D[]
  activeCam: number
  isCurrentlyPlayingAnimations: boolean
  hoveredMesh?: string | null
}

export default function Efects({ interactables, activeCam, isCurrentlyPlayingAnimations, hoveredMesh }: EffectsProps) {
  const camera = useThree((s) => s.camera)
  const visibleOutlineRef = useRef<OutlineEffect>(null)
  const hoverOutlineRef = useRef<OutlineEffect>(null)

  const hoveredMeshes = useMemo(() => {
    if (isCurrentlyPlayingAnimations) return []
    if (!hoveredMesh) return []
    return interactables.filter((obj) => obj.name.includes(hoveredMesh)) as Mesh[]
  }, [interactables, hoveredMesh, isCurrentlyPlayingAnimations])

  const visibleOutlines = useMemo(() => {
    if (isCurrentlyPlayingAnimations) return []
    const activeCamKey = `cam${activeCam + 1}`
    return interactables.filter((obj) => {
      if (hoveredMeshes.includes(obj as Mesh)) return false
      return isMarkerVisibleOnCam(obj.name, activeCamKey)
    })
  }, [interactables, activeCam, isCurrentlyPlayingAnimations, hoveredMeshes])

  // The EffectComposer rebuilds every EffectPass (recompiling their merged
  // shaders) whenever its `children` reference changes, so the composer subtree
  // is built once and the hovered/visible selection is pushed imperatively
  // below — otherwise every hover would rebuild the whole pipeline and hitch.
  const composer = useMemo(() => (
    <EffectComposer multisampling={0} autoClear={false}>
      {(!isMobile && <Bloom
          intensity={0.5}
          luminanceThreshold={0.95}
          luminanceSmoothing={0.025}
          mipmapBlur
          radius={0.6}
          levels={4}
      />) as any}
      <Outline
          ref={visibleOutlineRef}
          edgeStrength={OUTLINE_EDGE_STRENGTH}
          pulseSpeed={OUTLINE_PULSE_SPEED}
          visibleEdgeColor={OUTLINE_VISIBLE_COLOR}
          hiddenEdgeColor={OUTLINE_VISIBLE_COLOR}
          blur={false}
          xRay={false}
          kernelSize={KernelSize.SMALL}
          blendFunction={BlendFunction.ALPHA}
      />
      <Outline
          ref={hoverOutlineRef}
          selectionLayer={11}
          edgeStrength={OUTLINE_EDGE_STRENGTH * 2}
          pulseSpeed={0}
          visibleEdgeColor={OUTLINE_HOVER_COLOR}
          hiddenEdgeColor={OUTLINE_HOVER_COLOR}
          blur={false}
          xRay={false}
          kernelSize={KernelSize.SMALL}
          blendFunction={BlendFunction.ALPHA}
      />
      {(!isMobile && <N8AO
        aoRadius={1.0}
        distanceFalloff={1.0}
        intensity={2.0}
        halfRes
        screenSpaceRadius={false}
        aoSamples={8}
        denoiseSamples={4}
      />) as any}
      <Vignette offset={0.3} darkness={0.6} />
      <LUT lut={defaultLutTexture} tetrahedralInterpolation />
      <SMAA preset={SMAAPreset.MEDIUM} />
    </EffectComposer>
  ), [])

  // Push selections straight to the persistent effects so hover/camera changes
  // never touch the composer's children.
  useEffect(() => {
    visibleOutlineRef.current?.selection.set(visibleOutlines)
  }, [visibleOutlines])

  useEffect(() => {
    hoverOutlineRef.current?.selection.set(hoveredMeshes)
  }, [hoveredMeshes])

  // The app reuses a single camera object and only mutates camera.near per
  // viewpoint (cam1/cam11 = 0.5, others = 0.01), so OutlineEffect.mainCamera
  // never fires on a switch and the DepthComparisonMaterial keeps stale near/far
  // — which breaks the depth-occlusion test. Re-sync the camera settings on the
  // persistent effects each camera change.
  //
  // cam1/cam11 (same viewpoint, near 0.5) also read their marker as occluded so
  // its outline would vanish; force xRay on there so it always shows, while
  // every other camera keeps real occlusion (outlines hidden behind geometry).
  // Done here (not as a static prop) so it survives the wrapper recreating the
  // effect on the post-mount camera swap. The xRay setter is a no-op when
  // unchanged, so this won't recompile the shader needlessly.
  useEffect(() => {
    const xRay = activeCam === 0 || activeCam === 10
    for (const fx of [visibleOutlineRef.current, hoverOutlineRef.current]) {
      if (!fx) continue
      fx.mainCamera = camera
      fx.xRay = xRay
    }
  }, [activeCam, camera])

  return composer
}
